import React, { useState, useEffect } from 'react';
import { clearAuth } from '../../utils/auth';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, Building, Stethoscope,
  DollarSign, Shield, Settings, BarChart3, Bell,
  LogOut, Menu, X, Search, ChevronDown, ChevronRight,
  Inbox, List, Database, Plane, Activity, Key, Heart, Briefcase, UserPlus, Sparkles
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import MobileBottomNav from '../../components/MobileBottomNav';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationBell from '../../components/NotificationBell';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Pipeline icons mapping
const PIPELINE_ICONS = {
  'Sales Pipeline': Briefcase,
  'Customer Onboarding': UserPlus,
  'Pre Arrival and Engagement': Plane,
  'Patient Management System': Heart,
  'Patient Referral Management': Users
};

// Pipeline slug generator
const getPipelineSlug = (name) => {
  const slugMap = {
    'Sales Pipeline': 'sales',
    'Customer Onboarding': 'customer-onboarding',
    'Pre Arrival and Engagement': 'pre-arrival',
    'Patient Management System': 'patient-management',
    'Patient Referral Management': 'patient-referral'
  };
  return slugMap[name] || name.toLowerCase().replace(/\s+/g, '-');
};

// Mobile bottom nav items
const mobileNavItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/admin/dashboard' },
  { icon: Users, label: 'Users', path: '/admin/patients', matchPaths: ['/admin/patients', '/admin/doctors'] },
  { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
  { icon: DollarSign, label: 'Finance', path: '/admin/financial' },
  { icon: Settings, label: 'Settings', path: '/admin/system' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [biginPipelines, setBiginPipelines] = useState([]);
  const [pipelinesExpanded, setPipelinesExpanded] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change for mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    
    if (!userData) {
      navigate('/admin/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    const adminRoles = ['super_admin', 'admin', 'doctor_verifier', 'finance_admin', 'moderator', 'support_admin', 'case_manager'];
    if (!adminRoles.includes(parsedUser.role)) {
      navigate('/');
      return;
    }

    setUser(parsedUser);
    if (parsedUser.role === 'super_admin' || parsedUser.role === 'case_manager') {
      fetchBiginPipelines();
    }
  }, [navigate]);

  const fetchBiginPipelines = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/bigin/pipelines`);
      if (response.ok) {
        const data = await response.json();
        setBiginPipelines(data.pipelines || []);
      }
    } catch (error) {
    }
  };

  const handleLogout = async () => {
    await clearAuth();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', roles: ['super_admin', 'admin', 'doctor_verifier', 'finance_admin', 'moderator', 'support_admin'] },
    { icon: UserCheck, label: 'Super Admin', path: '/admin/super-admin', roles: ['super_admin'], badge: 'Assignments' },
    { icon: Database, label: 'Bigin CRM', path: '/admin/bigin', roles: ['super_admin', 'case_manager'] },
    { icon: Inbox, label: 'Inquiries', path: '/admin/inquiries', roles: ['super_admin'] },
    { icon: List, label: 'Classified', path: '/admin/classified', roles: ['super_admin', 'admin', 'hospital'] },
    { icon: Users, label: 'Patients', path: '/admin/patients', roles: ['super_admin', 'admin', 'support_admin'] },
    { icon: UserCheck, label: 'Doctors', path: '/admin/doctors', roles: ['super_admin', 'admin', 'doctor_verifier'] },
    { icon: Building, label: 'Hospitals', path: '/admin/hospitals', roles: ['super_admin', 'admin'] },
    { icon: Stethoscope, label: 'Treatments', path: '/admin/treatments', roles: ['super_admin', 'admin'] },
    { icon: DollarSign, label: 'Financial', path: '/admin/financial', roles: ['super_admin', 'admin', 'finance_admin'] },
    { icon: Shield, label: 'Moderation', path: '/admin/moderation', roles: ['super_admin', 'admin', 'moderator'] },
    { icon: Settings, label: 'System', path: '/admin/system', roles: ['super_admin'] },
    { icon: Key, label: 'Integrations', path: '/admin/integrations', roles: ['super_admin'] },
    { icon: Sparkles, label: 'AI Settings', path: '/admin/ai-settings', roles: ['super_admin', 'admin'] },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', roles: ['super_admin', 'admin'] },
  ];

  const hasAccess = (itemRoles) => {
    return user && itemRoles.includes(user.role);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="admin-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <span className="font-heading font-bold text-slate-900 dark:text-white">MedRahi Admin</span>
              <p className="text-xs text-slate-500 dark:text-slate-400">Control Panel</p>
            </div>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="h-[calc(100vh-140px)]">
          <nav className="p-4 space-y-1">
            {/* Menu Items before pipelines */}
            {menuItems.slice(0, 3).map((item) => {
              if (!hasAccess(item.roles)) return null;
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <Badge className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 text-[10px]">
                      {item.badge}
                    </Badge>
                  )}
                </NavLink>
              );
            })}

            {/* Pipelines Section */}
            {biginPipelines.length > 0 && (user?.role === 'super_admin' || user?.role === 'case_manager') && (
              <Collapsible open={pipelinesExpanded} onOpenChange={setPipelinesExpanded}>
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                    <div className="flex items-center gap-3">
                      <Activity className="w-5 h-5" strokeWidth={1.5} />
                      <span className="font-medium">Pipelines</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{biginPipelines.length}</Badge>
                      <ChevronRight className={`w-4 h-4 transition-transform ${pipelinesExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="ml-4 pl-4 border-l border-slate-200 dark:border-slate-700 space-y-1 mt-1">
                    {biginPipelines.map((pipeline) => {
                      const pipelineSlug = getPipelineSlug(pipeline.name);
                      const pipelinePath = `/admin/pipeline/${pipelineSlug}`;
                      const isActive = location.pathname === pipelinePath;
                      const PipelineIcon = PIPELINE_ICONS[pipeline.name] || Activity;
                      
                      return (
                        <NavLink
                          key={pipeline.id}
                          to={pipelinePath}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                            ${isActive
                              ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 font-medium'
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }
                          `}
                        >
                          <PipelineIcon className="w-4 h-4" strokeWidth={1.5} />
                          <span className="truncate">{pipeline.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Remaining menu items */}
            {menuItems.slice(3).map((item) => {
              if (!hasAccess(item.roles)) return null;
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
          <div className="h-16 px-4 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
              </button>
              
              {/* Search */}
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl">
                <Search className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Search..."
                  className="bg-transparent border-none outline-none text-sm w-48 lg:w-64 text-slate-700 dark:text-slate-300 placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ThemeToggle />
              
              <NotificationBell role={user?.role} />

              {/* User Profile */}
              <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer transition-colors">
                <div className="text-right hidden md:block">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{user?.name || user?.email}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <div className="w-9 h-9 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white font-semibold text-sm">
                  {(user?.name || user?.email || 'A')[0].toUpperCase()}
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8 min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav items={mobileNavItems} baseColor="purple" />
    </div>
  );
}
